import type Keycloak from 'keycloak-js';
import { loginPathFor } from './login-redirect';
import { removeAccessToken, setAccessToken } from './tokens';

let keycloakInstance: Keycloak | null = null;
let refreshPromise: Promise<string | null> | null = null;

/**
 * How long before expiry the background refresh renews the token, in seconds.
 *
 * The realm issues access and refresh tokens that expire together (access token
 * lifespan equals the SSO idle timeout), and only a refresh keeps the SSO session
 * alive. Renewing in the last seconds is not enough: a throttled background tab or a
 * short sleep skips the window, the refresh token is then dead and the user is logged
 * out. Five minutes leaves room for Chrome's once-a-minute timers in hidden tabs.
 */
export const TOKEN_REFRESH_MARGIN_SECONDS = 300;

/**
 * True when Keycloak rejected the refresh token (HTTP 400, e.g. "Token is not active"
 * or "Session not active"): the session is over and only a new login helps. Any other
 * failure (network, 5xx) is transient and the current token may still be usable.
 */
export function isSessionEndedError(error: unknown): boolean {
	if (typeof error !== 'object' || error === null) return false;
	const response = (error as { response?: { status?: number } }).response;
	return response?.status === 400;
}

// The app boots before Keycloak has finished initialising: route loaders run as soon as
// the router is created, well before the provider's init() resolves. Token requests wait
// for the session to be registered (or cleared) instead of concluding there is none.
let sessionSettled = false;
let sessionWaiters: Array<() => void> = [];

function settleSession(): void {
	sessionSettled = true;
	const waiters = sessionWaiters;
	sessionWaiters = [];
	for (const wake of waiters) wake();
}

/**
 * Resolves once the Keycloak session has been registered or cleared. While Keycloak's
 * `login-required` init redirects the page away, callers simply stay pending.
 */
export function waitForKeycloakSession(): Promise<void> {
	if (sessionSettled) return Promise.resolve();
	return new Promise((resolve) => sessionWaiters.push(resolve));
}

/**
 * Registers the Keycloak instance to be used for session management. This function should be called once when the Keycloak instance is initialized, typically in the authentication context provider. It allows the session management utilities to access the Keycloak instance for token handling and authentication state management.
 * @param instance - The initialized Keycloak instance to be registered for session management.
 * @returns void
 */
export function registerKeycloakSession(instance: Keycloak | null): void {
	keycloakInstance = instance;
	settleSession();
}

/**
 * Clears the Keycloak session by resetting the Keycloak instance and any related state. This function should be called when the user logs out or when the session needs to be invalidated. It ensures that all authentication-related information is cleared, including the access token, and that the application state is reset to reflect that the user is no longer authenticated.
 * @returns void
 */
export function clearKeycloakSession(): void {
	keycloakInstance = null;
	refreshPromise = null;
	removeAccessToken();
	settleSession();
}

/**
 * Retrieves the current Keycloak token. If a token is available, it sets the access token in the application's state management. If no token is available, it ensures that any existing access token is removed from the state. This function is used internally to get the current valid token for API requests and should not be called directly by components; instead, use `ensureFreshToken` to get a valid token that is guaranteed to be fresh.
 * @returns The current Keycloak token if available, or null if no token is present.
 */
function getCurrentToken(): string | null {
	const token = keycloakInstance?.token || null;
	if (token) {
		setAccessToken(token);
	} else {
		removeAccessToken();
	}
	return token;
}

function canRefreshToken(): boolean {
	if (!keycloakInstance?.authenticated || !keycloakInstance.refreshToken) {
		return false;
	}

	return true;
}

/**
 * Ensures that the Keycloak token is fresh and valid for at least `minValidity` seconds.
 * Waits for Keycloak initialisation to finish first (see `waitForKeycloakSession`).
 * If the token is expired or about to expire, it attempts to refresh it.
 * If `forceRefresh` is true, it will attempt to refresh the token regardless of its current validity.
 * Returns the current valid token or null if it cannot be refreshed.
 * This function also handles concurrent calls by sharing the same refresh promise, preventing multiple simultaneous refresh attempts.
 * @param minValidity - Minimum number of seconds the token should be valid for. Default is 30 seconds.
 * @param forceRefresh - If true, forces a token refresh regardless of current validity. Default is false.
 * @returns A promise that resolves to the current valid token or null if it cannot be refreshed.
 * @throws An error if the token refresh fails, which should be handled by the caller (e.g., by logging out the user).
 * @example
 * // Ensure the token is valid for at least 30 seconds before making an API call
 * const token = await ensureFreshToken();
 * if (token) {
 *   // Proceed with API call using the valid token
 * } else {
 *   // Handle case where token cannot be refreshed (e.g., redirect to login)
 * }
 */
export async function ensureFreshToken(minValidity = 30, forceRefresh = false): Promise<string | null> {
	await waitForKeycloakSession();

	// If there's no Keycloak instance, we can't refresh the token, so return null
	if (!keycloakInstance) {
		return null;
	}

	// A de-authenticated instance may still hold its last token. Storing or returning
	// it would keep the router's token check (routes/auth/middleware.ts) passing for
	// a user who is no longer signed in, so drop it and report "no session".
	if (!keycloakInstance.authenticated) {
		removeAccessToken();
		return null;
	}

	// Authenticated but without a refresh token: the current token is all there is
	if (!canRefreshToken()) {
		return getCurrentToken();
	}

	// If the token is still valid and no force refresh is requested, return the current token
	if (!forceRefresh && !keycloakInstance.isTokenExpired(minValidity)) {
		return getCurrentToken();
	}

	if (!refreshPromise) {
		refreshPromise = keycloakInstance
			.updateToken(forceRefresh ? -1 : minValidity)
			.then(() => getCurrentToken())
			.catch((error) => {
				// keycloak-js keeps the old token on transient failures and clears it on 400.
				// A token that is still valid stays in use; the next tick retries the refresh.
				if (!isSessionEndedError(error) && keycloakInstance?.token && !keycloakInstance.isTokenExpired(0)) {
					return getCurrentToken();
				}
				removeAccessToken();
				throw error;
			})
			.finally(() => {
				refreshPromise = null;
			});
	}

	return refreshPromise;
}

/**
 * Redirects the user to the login page. This function is called when the user is not authenticated or when token refresh fails.
 * It uses `window.location.assign` to navigate to the login page, which allows for a full page reload and ensures that the user is properly redirected to the authentication flow.
 * The current page is passed along as `returnTo`, so the user lands back on it after logging in.
 * This function should be called in scenarios where the user's session is no longer valid and they need to re-authenticate.
 * @example
 * // Redirect to login when token refresh fails
 * try {
 *   await ensureFreshToken();
 * } catch (error) {
 *   redirectToLogin();
 * }
 */
export function redirectToLogin(): void {
	window.location.assign(loginPathFor(window.location));
}
